/** Import prospects without requiring a CRM: paste for quick lists, or upload,
 * review the detected columns, and explicitly confirm the import. */

import React, { useMemo, useState } from 'react'
import { CheckCircle2, FileSpreadsheet } from 'lucide-react'

import { Modal } from '@/components/UI/Modal/Modal'
import { Button } from '@/components/UI/Button/Button'
import { useToast } from '@/contexts/ToastContext'
import { http } from '@/services/api'
import { useImportModal } from '@/stores'
import { parseCsvText } from './csvParser'

interface PasteImportResult {
  created: number
  updated: number
  skipped: number
  errored: number
  errors: Array<{ row?: number; error?: string }>
  companies_created: number
  enrichment_queued: boolean
}

interface CSVPreview {
  headers: string[]
  sample_rows: string[][]
  total_rows: number
  suggested_mapping: Record<string, string>
  import_job_id: string
}

interface CSVConfirmResult {
  import_job_id: string
  status: 'completed' | 'processing'
  message: string
  rows_created: number
  rows_updated: number
  rows_skipped: number
  rows_errored: number
}

interface CompletedImport {
  created: number
  updated: number
  skipped: number
  errored: number
  companiesCreated?: number
  processing?: boolean
}

const TARGET_FIELDS = [
  ['', 'Do not import'],
  ['email', 'Email'],
  ['name', 'Full name'],
  ['first_name', 'First name'],
  ['last_name', 'Last name'],
  ['company_name', 'Company'],
  ['domain', 'Company domain'],
  ['title', 'Job title'],
  ['phone', 'Phone'],
  ['linkedin_url', 'LinkedIn URL'],
] as const

const HEADER_TARGET: Record<string, string> = {
  name: 'name',
  'full name': 'name',
  full_name: 'name',
  email: 'email',
  'email address': 'email',
  email_address: 'email',
  company: 'company_name',
  'company name': 'company_name',
  company_name: 'company_name',
  organization: 'company_name',
  title: 'title',
  'job title': 'title',
  job_title: 'title',
  phone: 'phone',
  'phone number': 'phone',
  phone_number: 'phone',
  linkedin: 'linkedin_url',
  'linkedin url': 'linkedin_url',
  linkedin_url: 'linkedin_url',
  domain: 'domain',
}

export const CsvImportModal: React.FC = () => {
  const { open, closeModal } = useImportModal()
  const { addToast } = useToast()
  const [tab, setTab] = useState<'paste' | 'upload'>('paste')
  const [csvText, setCsvText] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [working, setWorking] = useState(false)
  const [preview, setPreview] = useState<CSVPreview | null>(null)
  const [mapping, setMapping] = useState<Record<string, string>>({})
  const [result, setResult] = useState<CompletedImport | null>(null)

  const mappedEmail = useMemo(() => Object.values(mapping).includes('email'), [mapping])

  const handlePasteImport = async () => {
    if (!csvText.trim()) return
    setWorking(true)
    try {
      const parsed = parseCsvText(csvText.trim())
      if (parsed.length < 2) throw new Error('Add a header row and at least one prospect row.')
      const sourceHeaders = parsed[0].map((header) => header.trim())
      const columns = sourceHeaders.map((header) => HEADER_TARGET[header.toLowerCase()] ?? '')
      if (!columns.includes('email')) throw new Error('Map a column named Email or Email Address. Email is required for prospect imports.')

      const response = await http<PasteImportResult>('/import/paste', {
        method: 'POST',
        body: { rows: parsed.slice(1), columns, import_mode: 'prospects', conflict_mode: 'update' },
      })
      setResult({
        created: response.created,
        updated: response.updated,
        skipped: response.skipped,
        errored: response.errored,
        companiesCreated: response.companies_created,
      })
      addToast({ type: 'success', title: 'Import complete', message: `${response.created} created, ${response.updated} updated.` })
    } catch (error) {
      addToast({ type: 'error', title: 'Import failed', message: error instanceof Error ? error.message : 'Try again.' })
    } finally {
      setWorking(false)
    }
  }

  const handleFilePreview = async () => {
    if (!file) return
    setWorking(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const response = await http<CSVPreview>('/import/csv', { method: 'POST', body: formData })
      setPreview(response)
      setMapping(response.suggested_mapping)
    } catch (error) {
      addToast({ type: 'error', title: 'Could not read file', message: error instanceof Error ? error.message : 'Try another CSV.' })
    } finally {
      setWorking(false)
    }
  }

  const confirmFileImport = async () => {
    if (!preview || !mappedEmail) return
    setWorking(true)
    try {
      const response = await http<CSVConfirmResult>('/import/csv/confirm', {
        method: 'POST',
        body: {
          import_job_id: preview.import_job_id,
          column_mapping: mapping,
          import_mode: 'prospects',
          conflict_mode: 'update',
        },
      })
      setResult({
        created: response.rows_created,
        updated: response.rows_updated,
        skipped: response.rows_skipped,
        errored: response.rows_errored,
        processing: response.status === 'processing',
      })
      addToast({ type: 'success', title: response.status === 'processing' ? 'Import started' : 'Import complete', message: response.message })
    } catch (error) {
      addToast({ type: 'error', title: 'Import failed', message: error instanceof Error ? error.message : 'Try again.' })
    } finally {
      setWorking(false)
    }
  }

  const handleClose = () => {
    setCsvText('')
    setFile(null)
    setPreview(null)
    setMapping({})
    setResult(null)
    setTab('paste')
    closeModal()
  }

  if (!open) return null

  return (
    <Modal isOpen={open} onClose={handleClose} title="Import prospects" size="xl">
      {result ? (
        <div className="space-y-5 py-4 text-center">
          <CheckCircle2 className="mx-auto h-10 w-10 text-[var(--state-success)]" />
          <div>
            <h3 className="text-lg font-semibold text-[var(--text-primary)]">{result.processing ? 'Import started' : 'Import complete'}</h3>
            <p className="mt-2 text-sm text-[var(--text-muted)]">
              {result.processing
                ? 'Your larger file is processing in the background.'
                : `${result.created} prospects created · ${result.updated} updated · ${result.skipped} skipped`}
            </p>
            {!result.processing && result.companiesCreated !== undefined && (
              <p className="mt-1 text-sm text-[var(--text-muted)]">{result.companiesCreated} accounts created from company context.</p>
            )}
            {result.errored > 0 && <p className="mt-2 text-sm text-[var(--state-danger)]">{result.errored} rows could not be imported.</p>}
          </div>
          <div className="flex justify-end"><Button onClick={handleClose} variant="primary">Done</Button></div>
        </div>
      ) : preview ? (
        <div className="space-y-5">
          <div className="rounded-lg border border-[var(--border)] bg-[var(--bg-elevated)] p-4">
            <div className="flex items-center gap-3">
              <FileSpreadsheet className="h-5 w-5 text-[var(--accent)]" />
              <div>
                <p className="font-semibold text-[var(--text-primary)]">{file?.name}</p>
                <p className="text-xs text-[var(--text-muted)]">{preview.total_rows} rows detected. Review where each column belongs.</p>
              </div>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {preview.headers.map((header) => (
              <label key={header} className="text-sm font-medium text-[var(--text-secondary)]">
                {header}
                <select
                  value={mapping[header] ?? ''}
                  onChange={(event) => setMapping((current) => ({ ...current, [header]: event.target.value }))}
                  className="mt-1.5 w-full rounded-lg border border-[var(--border)] bg-[var(--bg-canvas)] px-3 py-2.5 text-[var(--text-primary)]"
                >
                  {TARGET_FIELDS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </label>
            ))}
          </div>
          <div className="overflow-x-auto rounded-lg border border-[var(--border)]">
            <table className="w-full text-left text-xs">
              <thead className="bg-[var(--bg-elevated)] text-[var(--text-muted)]"><tr>{preview.headers.map((header) => <th key={header} className="px-3 py-2 font-semibold">{header}</th>)}</tr></thead>
              <tbody className="text-[var(--text-secondary)]">{preview.sample_rows.slice(0, 3).map((row, rowIndex) => <tr key={rowIndex} className="border-t border-[var(--border-muted)]">{preview.headers.map((header, columnIndex) => <td key={header} className="max-w-xs truncate px-3 py-2">{row[columnIndex]}</td>)}</tr>)}</tbody>
            </table>
          </div>
          {!mappedEmail && <p role="alert" className="text-sm text-[var(--state-danger)]">Choose which source column contains Email before importing.</p>}
          <div className="flex justify-between gap-2">
            <Button variant="outline" onClick={() => setPreview(null)}>Back</Button>
            <Button variant="primary" disabled={working || !mappedEmail} onClick={confirmFileImport}>{working ? 'Importing…' : `Import ${preview.total_rows} rows`}</Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex gap-2 border-b border-[var(--border)]">
            {(['paste', 'upload'] as const).map((value) => (
              <button key={value} className={`border-b-2 px-3 pb-2 text-sm font-medium ${tab === value ? 'border-[var(--accent)] text-[var(--accent)]' : 'border-transparent text-[var(--text-muted)]'}`} onClick={() => setTab(value)}>
                {value === 'paste' ? 'Paste CSV' : 'Upload file'}
              </button>
            ))}
          </div>
          {tab === 'paste' ? (
            <div className="space-y-3">
              <p className="text-sm text-[var(--text-muted)]">Paste a header row and prospect data. Email is required; company context creates or links the account automatically.</p>
              <textarea
                value={csvText}
                onChange={(event) => setCsvText(event.target.value)}
                placeholder={'name,email,company,title\nJane Smith,jane@acme.com,Acme Corp,VP Sales'}
                rows={7}
                className="w-full rounded-lg border border-[var(--border)] bg-[var(--bg-canvas)] p-3 font-mono text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent)]"
              />
              <div className="flex justify-end"><Button onClick={handlePasteImport} disabled={working || !csvText.trim()} variant="primary">{working ? 'Importing…' : 'Import prospects'}</Button></div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-[var(--text-muted)]">Upload a CSV, review its column mapping, then confirm. Uploading alone never changes your data.</p>
              <label className="block cursor-pointer rounded-lg border-2 border-dashed border-[var(--border)] p-8 text-center transition-colors hover:border-[var(--accent)]">
                <input type="file" accept=".csv,text/csv" className="sr-only" onChange={(event) => setFile(event.target.files?.[0] ?? null)} />
                <FileSpreadsheet className="mx-auto h-8 w-8 text-[var(--accent)]" />
                <p className="mt-3 text-sm font-medium text-[var(--text-primary)]">{file ? file.name : 'Choose a CSV file'}</p>
                <p className="mt-1 text-xs text-[var(--text-muted)]">Larger files continue in the background after confirmation</p>
              </label>
              <div className="flex justify-end"><Button onClick={handleFilePreview} disabled={working || !file} variant="primary">{working ? 'Reading…' : 'Review file'}</Button></div>
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}
